<template>
  <div 
    class="chat-container"
    ref="chatContainer"
    @scroll="handleScroll"
    style="height: calc(100vh - 300px); overflow-y: auto;"
  >
    <div v-if="searchQuery && messages.length === 0" class="no-results">
      No messages found matching "{{ searchQuery }}"
    </div>
    <div 
      v-for="(message, index) in messages" 
      :key="index" 
      :id="'message-' + index"
      class="message"
      :class="{ 'highlighted': isMessageHighlighted(index) }"
    >
      <div class="message-header">
        <span class="message-author">{{ message.sender_alias }}</span>
        <div class="message-meta">
          <span v-if="message.translated" class="translated-pill">Translated</span>
          <span class="message-time">{{ formatTimestamp(message.timestamp) }}</span>
        </div>
      </div>
      <div class="message-content" v-html="highlightSearchTerm(message.message)"></div>
      <div v-if="message.chat_id" class="message-chat-id">
        Chat ID: {{ message.chat_id }}
      </div>
    </div>
    <div v-if="isLoading" class="loading-indicator">
      Loading more messages...
    </div>
  </div>
</template>

<script>
import { marked } from 'marked';
import DOMPurify from 'dompurify';

export default {
  name: 'MessageList',
  props: {
    messages: {
      type: Array,
      required: true
    },
    searchQuery: {
      type: String,
      default: ''
    },
    highlightedMessageId: {
      type: Number,
      default: null
    },
    isLoading: {
      type: Boolean,
      default: false
    }
  },
  emits: ['scroll'],
  methods: {
    handleScroll(event) {
      this.$emit('scroll', event);
    },
    formatTimestamp(timestamp) {
      const [datePart, timePart] = timestamp.split(' ');
      const [year, month, day] = datePart.split('-');
      return `${month}/${day}/${year} ${timePart}`;
    },
    highlightSearchTerm(text) {
      if (!this.searchQuery) {
        return DOMPurify.sanitize(marked(text));
      }

      const escapedQuery = this.escapeRegExp(this.searchQuery);
      const regex = new RegExp(`(${escapedQuery})`, 'gi');
      const highlightedText = text.replace(regex, '<mark>$1</mark>');
      return DOMPurify.sanitize(marked(highlightedText));
    },
    escapeRegExp(string) {
      return string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    },
    isMessageHighlighted(index) {
      return this.highlightedMessageId === index;
    }
  }
}
</script>

<style scoped>
.chat-container {
  padding: 1rem;
  scrollbar-width: none; /* Firefox */
  -ms-overflow-style: none; /* IE and Edge */
}

.chat-container::-webkit-scrollbar {
  display: none; /* Chrome, Safari, Opera */
}

.message {
  background-color: rgba(26, 26, 26, 0.8);
  border: 1px solid var(--border);
  border-radius: 8px;
  padding: 1rem;
  margin-bottom: 1rem;
}

.message.highlighted {
  border-color: var(--primary);
  box-shadow: 0 0 0 2px var(--primary);
}

.message-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0.5rem;
}

.message-author {
  font-weight: 600;
  color: var(--primary);
}

.message-meta {
  display: flex;
  gap: 0.5rem;
  align-items: center;
}

.translated-pill {
  background-color: var(--primary);
  color: white;
  padding: 0.25rem 0.5rem;
  border-radius: 4px;
  font-size: 0.8rem;
}

.message-time {
  color: var(--text-secondary);
  font-size: 0.9rem;
}

.message-content {
  color: var(--text-secondary);
  line-height: 1.5;
  white-space: pre-wrap;
}

.message-content :deep(a) {
  color: var(--primary);
  text-decoration: none;
  transition: color 0.2s;
}

.message-content :deep(a:hover) {
  color: var(--text-primary);
  text-decoration: underline;
}

.message-content :deep(mark) {
  background-color: var(--primary);
  color: white;
  padding: 0.1rem 0.2rem;
  border-radius: 2px;
}

.message-chat-id {
  margin-top: 0.5rem;
  color: var(--text-secondary);
  font-size: 0.9rem;
}

.no-results {
  text-align: center;
  color: var(--text-secondary);
  padding: 2rem;
}

.loading-indicator {
  text-align: center;
  color: var(--text-secondary);
  padding: 1rem;
}
</style> 